import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Button from "@material-ui/core/Button";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    height: "100%"
  },
  step: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2)
  },
  stepNumber: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    color: "#fff",
    // No escuro o boxticket some no fundo do cartão: tom claro translúcido.
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.2)" : theme.palette.primary.main
  },
  // Prévia no formato de conversa do WhatsApp (balão enviado pela empresa).
  chat: {
    padding: theme.spacing(2),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "#0b141a" : "#efeae2",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  bubbleOut: {
    alignSelf: "flex-end",
    maxWidth: "85%",
    padding: theme.spacing(1, 1.5),
    borderRadius: "8px 0 8px 8px",
    backgroundColor: theme.palette.type === "dark" ? "#005c4b" : "#d9fdd3",
    color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: 14
  },
  bubbleIn: {
    alignSelf: "flex-start",
    padding: theme.spacing(1, 1.5),
    borderRadius: "0 8px 8px 8px",
    backgroundColor: theme.palette.type === "dark" ? "#202c33" : "#ffffff",
    color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
    fontSize: 14
  }
}));

// Mesmo formato do WhatsApp: *negrito* e _itálico_ só na prévia.
const renderWhatsappText = text =>
  String(text || "")
    .split(/(\*[^*\n]+\*|_[^_\n]+_)/g)
    .map((part, index) => {
      if (/^\*[^*\n]+\*$/.test(part)) return <strong key={index}>{part.slice(1, -1)}</strong>;
      if (/^_[^_\n]+_$/.test(part)) return <em key={index}>{part.slice(1, -1)}</em>;
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });

const previewName = text =>
  String(text || "")
    .replace(/{{\s*firstName\s*}}/g, "Maria")
    .replace(/{{\s*name\s*}}/g, "Maria Silva");

const RatingsSettings = ({ onSaved }) => {
  const classes = useStyles();
  const [form, setForm] = useState(null);
  const [scale, setScale] = useState({ min: 0, max: 10 });
  const [saving, setSaving] = useState(false);

  const applyData = data => {
    setForm({
      enabled: Boolean(data.enabled),
      message: data.message || "",
      thanksMessage: data.thanksMessage || "",
      windowHours: String(data.windowHours || 24)
    });
    if (data.scale) setScale(data.scale);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ticket-ratings/settings");
        applyData(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const handleChange = field => e => {
    const value = field === "enabled" ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/ticket-ratings/settings", {
        ...form,
        windowHours: Number(form.windowHours)
      });
      applyData(data);
      toast.success(i18n.t("ticketRatings.settings.saved"));
      if (onSaved) onSaved();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  if (!form) return null;

  const windowHours = Number(form.windowHours);
  const windowInvalid =
    !Number.isInteger(windowHours) || windowHours < 1 || windowHours > 168;
  const messageInvalid = !form.message.trim();

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <Paper className={classes.paper}>
          <Typography variant="h6" color="primary" gutterBottom>
            {i18n.t("ticketRatings.settings.title")}
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                onChange={handleChange("enabled")}
                color="primary"
              />
            }
            label={i18n.t("ticketRatings.settings.enabled")}
          />

          <TextField
            label={i18n.t("ticketRatings.settings.message")}
            value={form.message}
            onChange={handleChange("message")}
            multiline
            minRows={4}
            fullWidth
            variant="outlined"
            margin="normal"
            error={messageInvalid}
            helperText={i18n.t("ticketRatings.settings.messageHelp", {
              min: scale.min,
              max: scale.max,
              // Mostra as variáveis literalmente: sem valor o i18next as apagaria.
              firstName: "{{firstName}}",
              name: "{{name}}",
              interpolation: { escapeValue: false }
            })}
          />

          <TextField
            label={i18n.t("ticketRatings.settings.thanksMessage")}
            value={form.thanksMessage}
            onChange={handleChange("thanksMessage")}
            multiline
            minRows={2}
            fullWidth
            variant="outlined"
            margin="normal"
            helperText={i18n.t("ticketRatings.settings.thanksMessageHelp")}
          />

          <TextField
            label={i18n.t("ticketRatings.settings.windowHours")}
            value={form.windowHours}
            onChange={handleChange("windowHours")}
            type="number"
            inputProps={{ min: 1, max: 168, step: 1 }}
            variant="outlined"
            margin="normal"
            error={windowInvalid}
            helperText={i18n.t("ticketRatings.settings.windowHoursHelp")}
          />

          <div style={{ marginTop: 16 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || windowInvalid || messageInvalid}
            >
              {saving
                ? i18n.t("ticketRatings.settings.saving")
                : i18n.t("ticketRatings.settings.save")}
            </Button>
          </div>
        </Paper>
      </Grid>

      <Grid item xs={12} md={5}>
        <Paper className={classes.paper}>
          <Typography variant="h6" color="primary" gutterBottom>
            {i18n.t("ticketRatings.settings.howItWorks")}
          </Typography>
          {["close", "reply", "noReply"].map((step, index) => (
            <div key={step} className={classes.step}>
              <span className={classes.stepNumber}>{index + 1}</span>
              <Typography variant="body2">
                {i18n.t(`ticketRatings.settings.steps.${step}`, {
                  min: scale.min,
                  max: scale.max,
                  hours: form.windowHours
                })}
              </Typography>
            </div>
          ))}

          <Typography variant="subtitle2" gutterBottom>
            {i18n.t("ticketRatings.settings.preview")}
          </Typography>
          <div className={classes.chat}>
            <div className={classes.bubbleOut}>
              {renderWhatsappText(previewName(form.message))}
            </div>
            <div className={classes.bubbleIn}>9</div>
            {form.thanksMessage.trim() && (
              <div className={classes.bubbleOut}>
                {renderWhatsappText(previewName(form.thanksMessage))}
              </div>
            )}
          </div>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default RatingsSettings;
